import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Pagination } from '@/components/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { NoSenderWarning } from '@/components/NoSenderWarning'
import { EnrollStartField, type EnrollStartMode } from '@/components/EnrollStartField'
import { scheduledStartToUtc } from '@/lib/dates'
import { emailDomain } from '@/lib/email'
import { RefreshCw, Search, Users, UserPlus, Loader2, ListFilter, AlertTriangle, ArrowUp, ArrowDown, ArrowUpDown, Upload, Plus } from 'lucide-react'
import {
  useContacts,
  useSegments,
  useSequences,
  usePreviewBulkEnrollments,
  useCreateBulkEnrollments,
  useAvailableProperties,
} from '@/hooks/useApi'
import type { BulkEnrollmentPreview, ContactSortField, EnrollOverridesMap } from '@/types/api'
import { NumericInput } from '@/components/ui/numeric-input'
import { EnrollSegmentDialog } from '@/components/EnrollSegmentDialog'
import {
  HubSpotSyncDialog,
  SYNC_FROM_SEGMENT_PARAM,
  type HubSpotSyncMode,
} from '@/components/HubSpotSyncDialog'
import { ContactCreateDialog } from '@/components/ContactCreateDialog'
import { ContactImportDialog } from '@/components/ContactImportDialog'
import { ContactCompany } from '@/components/CompanyLogo'
import { ContactPreviewList, ContactPreviewPanel } from '@/components/ContactPreviewPanel'
import { pruneEmptyOverrides } from '@/lib/overrides'
import { ViewModeSwitch } from '@/components/ViewModeSwitch'
import { useViewModeParam } from '@/hooks/useViewMode'
import { useDebounce } from '@/hooks/useDebounce'

function SortableHead({
  label,
  column,
  sortBy,
  sortOrder,
  onSort,
  className,
}: {
  label: string
  column: ContactSortField
  sortBy: ContactSortField | null
  sortOrder: 'asc' | 'desc'
  onSort: (column: ContactSortField) => void
  className?: string
}) {
  const active = sortBy === column
  const Icon = active ? (sortOrder === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {label}
        <Icon aria-hidden className={`h-3.5 w-3.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </TableHead>
  )
}

export function Contacts() {
  const { t } = useTranslation(['contacts', 'common'])
  const viewMode = useViewModeParam()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set())
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false)
  const [syncDialogOpen, setSyncDialogOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [selectedSequence, setSelectedSequence] = useState('')
  const [startMode, setStartMode] = useState<EnrollStartMode>('now')
  const [startAtLocal, setStartAtLocal] = useState('')
  const [syncMode, setSyncMode] = useState<HubSpotSyncMode>('all')
  const [filterSegmentId, setFilterSegmentId] = useState<string>('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [enrollmentStatus, setEnrollmentStatus] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [hasHubspot, setHasHubspot] = useState('')
  const [doNotContactFilter, setDoNotContactFilter] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [minEnrollments, setMinEnrollments] = useState<number>(0)
  const [maxEnrollments, setMaxEnrollments] = useState<number>(0)
  const [propertyKey, setPropertyKey] = useState('')
  const [propertyValue, setPropertyValue] = useState('')
  const [sortBy, setSortBy] = useState<ContactSortField | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [searchParams, setSearchParams] = useSearchParams()

  // /segments links here to import a HubSpot list: open the sync dialog on it (#318)
  useEffect(() => {
    if (searchParams.get(SYNC_FROM_SEGMENT_PARAM.name) !== SYNC_FROM_SEGMENT_PARAM.value) return
    setSyncMode('segment')
    setSyncDialogOpen(true)
    setSearchParams(
      (params) => {
        params.delete(SYNC_FROM_SEGMENT_PARAM.name)
        return params
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  const debouncedCompany = useDebounce(companyFilter, 300)
  const debouncedPropertyValue = useDebounce(propertyValue, 300)

  const { data: availableProperties } = useAvailableProperties()

  const activeFilterCount = [
    !!enrollmentStatus,
    !!debouncedCompany,
    !!hasHubspot,
    !!doNotContactFilter,
    !!createdFrom,
    !!createdTo,
    minEnrollments > 0,
    maxEnrollments > 0,
    !!(propertyKey && debouncedPropertyValue),
  ].filter(Boolean).length

  const { data: contacts, isLoading } = useContacts(page, 50, {
    search: debouncedSearch || undefined,
    segmentId: filterSegmentId || undefined,
    enrollmentStatus: enrollmentStatus || undefined,
    company: debouncedCompany || undefined,
    hasHubspot: hasHubspot || undefined,
    doNotContact: doNotContactFilter || undefined,
    createdFrom: createdFrom || undefined,
    createdTo: createdTo || undefined,
    minEnrollments: minEnrollments > 0 ? String(minEnrollments) : undefined,
    maxEnrollments: maxEnrollments > 0 ? String(maxEnrollments) : undefined,
    propertyKey: propertyKey && debouncedPropertyValue ? propertyKey : undefined,
    propertyValue: propertyKey && debouncedPropertyValue ? debouncedPropertyValue : undefined,
    viewMode,
    sortBy: sortBy ?? undefined,
    sortOrder,
  })

  const handleSort = (column: ContactSortField) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
    setPage(1)
  }
  const { data: sequences } = useSequences()
  const { data: localSegments } = useSegments()
  const segmentItems = localSegments?.items ?? []
  const previewBulkEnrollments = usePreviewBulkEnrollments()
  const createBulkEnrollments = useCreateBulkEnrollments()
  const [enrollPreview, setEnrollPreview] = useState<BulkEnrollmentPreview | null>(null)
  const [enrollSegmentDialogOpen, setEnrollSegmentDialogOpen] = useState(false)
  const [previewContactId, setPreviewContactId] = useState<string | null>(null)
  const [overridesByContact, setOverridesByContact] = useState<EnrollOverridesMap>({})

  const handleSelectAll = () => {
    if (!contacts) return
    if (selectedContacts.size === contacts.items.length) {
      setSelectedContacts(new Set())
    } else {
      setSelectedContacts(new Set(contacts.items.map((c) => c.id)))
    }
  }

  const handleResetFilters = () => {
    setEnrollmentStatus('')
    setCompanyFilter('')
    setHasHubspot('')
    setDoNotContactFilter('')
    setCreatedFrom('')
    setCreatedTo('')
    setMinEnrollments(0)
    setMaxEnrollments(0)
    setPropertyKey('')
    setPropertyValue('')
    setPage(1)
  }

  const handleSelectContact = (id: string) => {
    const newSelected = new Set(selectedContacts)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedContacts(newSelected)
  }

  const handleEnrollPreview = () => {
    if (!selectedSequence || selectedContacts.size === 0) return
    previewBulkEnrollments.mutate(
      {
        contact_ids: Array.from(selectedContacts),
        sequence_id: selectedSequence,
      },
      {
        onSuccess: (preview) => {
          // Something to tell before enrolling: a history to confirm, or
          // contacts marked "do not contact" that are left out (#372)
          if (preview.warnings.length > 0 || preview.skipped_do_not_contact > 0) {
            setEnrollPreview(preview)
          } else {
            doEnroll(false)
          }
        },
      }
    )
  }

  const doEnroll = (force: boolean) => {
    if (!selectedSequence || selectedContacts.size === 0) return
    const start_at = scheduledStartToUtc(startMode, startAtLocal, sequenceForEnroll?.timezone)
    const overrides = pruneEmptyOverrides(overridesByContact)
    createBulkEnrollments.mutate(
      {
        contact_ids: Array.from(selectedContacts),
        sequence_id: selectedSequence,
        force,
        start_at,
        ...(overrides ? { overrides } : {}),
      },
      {
        onSuccess: (result) => {
          setEnrollDialogOpen(false)
          setEnrollPreview(null)
          setSelectedContacts(new Set())
          setSelectedSequence('')
          setStartMode('now')
          setStartAtLocal('')
          setPreviewContactId(null)
          setOverridesByContact({})
          alert(
            result.skipped_do_not_contact > 0
              ? t('enrollDialog.enrolledResultDoNotContact', {
                  enrolled: result.enrolled,
                  skipped: result.skipped,
                  doNotContact: result.skipped_do_not_contact,
                })
              : t('enrollDialog.enrolledResult', { enrolled: result.enrolled, skipped: result.skipped }),
          )
        },
      }
    )
  }

  const activeSequences = sequences?.filter((s) => s.status === 'active') || []
  const enrollTargetHasNoSender = activeSequences.some(
    (s) => s.id === selectedSequence && !s.email_account_id,
  )
  const sequenceForEnroll = activeSequences.find((s) => s.id === selectedSequence)
  // Drill-down anteprima per contatto (#220): solo i selezionati della pagina corrente
  const selectedContactItems = (contacts?.items ?? []).filter((c) => selectedContacts.has(c.id))
  const previewContactForEnroll = selectedContactItems.find((c) => c.id === previewContactId)

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{t('list.title')}</h1>
        <div className="flex gap-2">
          <ViewModeSwitch />
          {selectedContacts.size > 0 && (
            <Button onClick={() => setEnrollDialogOpen(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              {t('list.enrollInSequence', { count: selectedContacts.size })}
            </Button>
          )}
          <Button variant="outline" onClick={() => setEnrollSegmentDialogOpen(true)}>
            <Users className="w-4 h-4 mr-2" />
            {t('list.enrollSegment')}
          </Button>
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            {t('list.importCsv')}
          </Button>
          <Button variant="outline" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('list.addContact')}
          </Button>
          <Button variant="outline" onClick={() => setSyncDialogOpen(true)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('list.syncFromHubspot')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('list.yourContacts')}</CardTitle>
            <div className="flex items-center gap-2">
              {segmentItems.length > 0 && (
                <Select
                  value={filterSegmentId}
                  onValueChange={(v) => {
                    setFilterSegmentId(v === 'all' ? '' : v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger className="w-48">
                    <ListFilter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder={t('filters.allSegments')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filters.allSegments')}</SelectItem>
                    {segmentItems.map((seg) => (
                      <SelectItem key={seg.id} value={seg.id}>
                        {seg.name} ({seg.contact_count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                <ListFilter className="w-4 h-4 mr-2" />
                {t('filters.title')}
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('list.searchPlaceholder')}
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value)
                    setPage(1)
                  }}
                  className="pl-9 w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        {filtersOpen && (
          <div className="px-6 pb-4 border-b">
            <div className="grid grid-cols-3 gap-4">
              {/* Enrollment Status */}
              <div className="space-y-1">
                <label className="text-sm text-muted-foreground">{t('filters.enrollmentStatus')}</label>
                <Select value={enrollmentStatus} onValueChange={(v) => { setEnrollmentStatus(v === 'all' ? '' : v); setPage(1) }}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('filters.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filters.all')}</SelectItem>
                    <SelectItem value="active">{t('filters.active')}</SelectItem>
                    <SelectItem value="paused">{t('filters.paused')}</SelectItem>
                    <SelectItem value="completed">{t('filters.completed')}</SelectItem>
                    <SelectItem value="replied">{t('filters.replied')}</SelectItem>
                    <SelectItem value="bounced">{t('filters.bounced')}</SelectItem>
                    <SelectItem value="unsubscribed">{t('filters.unsubscribed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Company */}
              <div className="space-y-1">
                <label className="text-sm text-muted-foreground">{t('filters.company')}</label>
                <Input
                  placeholder={t('filters.companyPlaceholder')}
                  value={companyFilter}
                  onChange={(e) => { setCompanyFilter(e.target.value); setPage(1) }}
                />
              </div>

              {/* HubSpot Sync */}
              <div className="space-y-1">
                <label className="text-sm text-muted-foreground">{t('filters.hubspotSynced')}</label>
                <Select value={hasHubspot} onValueChange={(v) => { setHasHubspot(v === 'all' ? '' : v); setPage(1) }}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('filters.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filters.all')}</SelectItem>
                    <SelectItem value="true">{t('filters.yes')}</SelectItem>
                    <SelectItem value="false">{t('filters.no')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Do not contact (#372) */}
              <div className="space-y-1">
                <label className="text-sm text-muted-foreground">{t('filters.doNotContact')}</label>
                <Select value={doNotContactFilter} onValueChange={(v) => { setDoNotContactFilter(v === 'all' ? '' : v); setPage(1) }}>
                  <SelectTrigger aria-label={t('filters.doNotContact')}>
                    <SelectValue placeholder={t('filters.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filters.all')}</SelectItem>
                    <SelectItem value="true">{t('filters.doNotContactOnly')}</SelectItem>
                    <SelectItem value="false">{t('filters.doNotContactExclude')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range */}
              <div className="space-y-1">
                <label className="text-sm text-muted-foreground">{t('filters.createdFrom')}</label>
                <Input
                  type="date"
                  value={createdFrom}
                  onChange={(e) => { setCreatedFrom(e.target.value); setPage(1) }}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm text-muted-foreground">{t('filters.createdTo')}</label>
                <Input
                  type="date"
                  value={createdTo}
                  onChange={(e) => { setCreatedTo(e.target.value); setPage(1) }}
                />
              </div>

              {/* Active Enrollments */}
              <div className="space-y-1">
                <label className="text-sm text-muted-foreground">{t('table.activeEnrollments')}</label>
                <div className="flex gap-2">
                  <NumericInput
                    placeholder={t('filters.min')}
                    value={minEnrollments}
                    onChange={(v) => { setMinEnrollments(v); setPage(1) }}
                    min={0}
                  />
                  <NumericInput
                    placeholder={t('filters.max')}
                    value={maxEnrollments}
                    onChange={(v) => { setMaxEnrollments(v); setPage(1) }}
                    min={0}
                  />
                </div>
              </div>

              {/* Custom Property */}
              <div className="space-y-1 col-span-2">
                <label className="text-sm text-muted-foreground">{t('filters.customProperty')}</label>
                <div className="flex gap-2">
                  <Select value={propertyKey} onValueChange={(v) => { setPropertyKey(v === 'none' ? '' : v); setPage(1) }}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder={t('filters.selectProperty')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('filters.none')}</SelectItem>
                      {availableProperties
                        ?.filter((p) => !p.builtin)
                        .map((p) => (
                          <SelectItem key={p.key} value={p.key}>
                            {p.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {propertyKey && (
                    <Input
                      placeholder={t('filters.filterValuePlaceholder')}
                      value={propertyValue}
                      onChange={(e) => { setPropertyValue(e.target.value); setPage(1) }}
                      className="flex-1"
                    />
                  )}
                </div>
              </div>

              {/* Reset */}
              <div className="flex items-end justify-end">
                <Button variant="ghost" size="sm" onClick={handleResetFilters}>
                  {t('filters.reset')}
                </Button>
              </div>
            </div>
          </div>
        )}
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : contacts && contacts.items.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          contacts.items.length > 0 &&
                          selectedContacts.size === contacts.items.length
                        }
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <SortableHead label={t('table.name')} column="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHead label={t('table.email')} column="email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHead label={t('table.qualifica')} column="jobtitle" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHead label={t('table.company')} column="company" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHead label={t('table.emailDomain')} column="email_domain" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHead label={t('table.owner')} column="owner" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHead label={t('table.saluto')} column="saluto" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHead label={t('table.activeEnrollments')} column="active_enrollments" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="text-center" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.items.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedContacts.has(contact.id)}
                          onCheckedChange={() => handleSelectContact(contact.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <Link
                          to={`/contacts/${contact.id}`}
                          className="hover:underline text-primary"
                        >
                          {contact.full_name || t('list.unknown')}
                        </Link>
                        {contact.do_not_contact_at && (
                          <Badge variant="destructive" className="ml-2">
                            {t('list.doNotContact')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.properties.jobtitle || '-'}</TableCell>
                      <TableCell><ContactCompany contact={contact} /></TableCell>
                      <TableCell>{emailDomain(contact.email) || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {contact.hubspot_owner_email || '-'}
                      </TableCell>
                      <TableCell>{contact.properties.saluto || '-'}</TableCell>
                      <TableCell className="text-center">
                        {contact.active_enrollment_count > 0 ? (
                          <Badge variant="secondary">
                            {contact.active_enrollment_count}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={page}
                totalPages={contacts.pages}
                onPageChange={setPage}
              />
            </>
          ) : (
            <EmptyState
              icon={Users}
              title={debouncedSearch ? t('empty.noContactsFound') : t('empty.noContactsYet')}
              description={
                debouncedSearch
                  ? t('empty.tryDifferentSearch')
                  : t('empty.importToStart')
              }
              action={
                !debouncedSearch
                  ? {
                      label: t('list.importCsv'),
                      onClick: () => setImportDialogOpen(true),
                    }
                  : undefined
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Enroll Dialog */}
      <Dialog open={enrollDialogOpen} onOpenChange={(open) => {
        setEnrollDialogOpen(open)
        if (!open) {
          setEnrollPreview(null)
          setStartMode('now')
          setStartAtLocal('')
          setPreviewContactId(null)
          setOverridesByContact({})
        }
      }}>
        <DialogContent className={previewContactForEnroll ? 'sm:max-w-2xl' : undefined}>
          {previewContactForEnroll && selectedSequence ? (
            <ContactPreviewPanel
              sequenceId={selectedSequence}
              contact={{
                id: previewContactForEnroll.id,
                name: previewContactForEnroll.full_name || previewContactForEnroll.email,
                email: previewContactForEnroll.email,
              }}
              draftOverrides={overridesByContact[previewContactForEnroll.id] ?? {}}
              onDraftChange={(next) =>
                setOverridesByContact((prev) => ({ ...prev, [previewContactForEnroll.id]: next }))
              }
              onBack={() => setPreviewContactId(null)}
            />
          ) : (
          <>
          <DialogHeader>
            <DialogTitle>{t('enrollDialog.title')}</DialogTitle>
            <DialogDescription>
              {enrollPreview
                ? enrollPreview.warnings.length > 0
                  ? t('enrollDialog.needConfirm')
                  : t('enrollDialog.needReview')
                : t('enrollDialog.selectToEnroll', { count: selectedContacts.size })
              }
            </DialogDescription>
          </DialogHeader>

          {!enrollPreview ? (
            <>
              <div className="py-4">
                <SearchableSelect
                  value={selectedSequence}
                  onValueChange={setSelectedSequence}
                  options={activeSequences.map((sequence) => ({ value: sequence.id, label: sequence.name }))}
                  placeholder={t('enrollDialog.selectSequence')}
                  searchPlaceholder={t('enrollDialog.searchSequence')}
                  emptyText={t('enrollDialog.noActiveSequences')}
                  disabled={activeSequences.length === 0}
                />
                {activeSequences.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('enrollDialog.createFirst')}
                  </p>
                )}
                {enrollTargetHasNoSender && (
                  <div className="mt-3">
                    <NoSenderWarning />
                  </div>
                )}
                {sequenceForEnroll && (
                  <div className="mt-4">
                    <EnrollStartField
                      mode={startMode}
                      onModeChange={setStartMode}
                      value={startAtLocal}
                      onValueChange={setStartAtLocal}
                      timezone={sequenceForEnroll.timezone}
                    />
                  </div>
                )}
                {/* Drill-down anteprima e override per contatto (#220) */}
                {sequenceForEnroll && (
                  <div className="mt-4">
                    <ContactPreviewList
                      contacts={selectedContactItems}
                      overridesByContact={overridesByContact}
                      onSelect={(contact) => setPreviewContactId(contact.id)}
                    />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEnrollDialogOpen(false)}>
                  {t('common:cancel')}
                </Button>
                <Button
                  onClick={handleEnrollPreview}
                  disabled={!selectedSequence || previewBulkEnrollments.isPending || (startMode === 'scheduled' && !startAtLocal)}
                >
                  {previewBulkEnrollments.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('enrollDialog.checking')}
                    </>
                  ) : (
                    t('enrollDialog.enroll')
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-3">
                {enrollPreview.ready > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t('enrollDialog.ready', { count: enrollPreview.ready })}
                  </p>
                )}
                {enrollPreview.skipped_active > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t('enrollDialog.skippedActive', { count: enrollPreview.skipped_active })}
                  </p>
                )}
                {enrollPreview.skipped_do_not_contact > 0 && (
                  <p className="text-sm text-red-700 dark:text-red-400">
                    {t('enrollDialog.skippedDoNotContact', { count: enrollPreview.skipped_do_not_contact })}
                  </p>
                )}
                {enrollPreview.warnings.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    {t('enrollDialog.warning', { count: enrollPreview.warnings.length })}
                  </div>
                  <ul className="space-y-1.5">
                    {enrollPreview.warnings.map((w) => (
                      <li key={w.contact_id} className="text-sm flex items-baseline gap-2">
                        <Badge variant={w.previous_status === 'bounced' ? 'destructive' : 'secondary'} className="text-xs shrink-0">
                          {w.previous_status}
                        </Badge>
                        <span className="text-muted-foreground">
                          {w.contact_name || w.contact_email} &mdash; {w.reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                )}
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setEnrollPreview(null)}>
                  {t('common:back')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => doEnroll(false)}
                  disabled={createBulkEnrollments.isPending || enrollPreview.ready === 0}
                >
                  {createBulkEnrollments.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  {enrollPreview.warnings.length > 0
                    ? t('enrollDialog.excludeAndProceed', { count: enrollPreview.ready })
                    : t('enrollDialog.proceed', { count: enrollPreview.ready })}
                </Button>
                {enrollPreview.warnings.length > 0 && (
                <Button
                  onClick={() => doEnroll(true)}
                  disabled={createBulkEnrollments.isPending}
                >
                  {createBulkEnrollments.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  {t('enrollDialog.enrollAll', { count: enrollPreview.ready + enrollPreview.warnings.length })}
                </Button>
                )}
              </DialogFooter>
            </>
          )}
          </>
          )}
        </DialogContent>
      </Dialog>

      <ContactCreateDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />

      <ContactImportDialog open={importDialogOpen} onOpenChange={setImportDialogOpen} />

      {/* Sync Dialog */}
      <HubSpotSyncDialog
        open={syncDialogOpen}
        onOpenChange={setSyncDialogOpen}
        mode={syncMode}
        onModeChange={setSyncMode}
      />

      {/* Enroll Segment Dialog */}
      <EnrollSegmentDialog
        open={enrollSegmentDialogOpen}
        onClose={() => setEnrollSegmentDialogOpen(false)}
      />
    </div>
  )
}
